const en = {
  common: {
    loading: "Loading...",
    dismiss: "Dismiss",
    status: "Status",
    providers: {
      google: "Gmail",
      microsoft: "Outlook",
    },
    languages: {
      en: "English",
      sk: "Slovak",
    },
  },
  auth: {
    login: {
      title: "Welcome Back",
      subtitle: "Sign in to manage your subscriptions",
      submit: "Sign In",
      submitting: "Signing in...",
      noAccount: "Don't have an account?",
      success: "Login successful!",
      failed: "Login failed",
    },
    signup: {
      title: "Create Account",
      subtitle: "Start tracking your subscriptions today",
      submit: "Create Account",
      submitting: "Creating account...",
      hasAccount: "Already have an account?",
      passwordMismatch: "Passwords do not match",
      success: "Account created successfully!",
      failed: "Signup failed",
    },
    fields: {
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      signUp: "Sign up",
      signIn: "Sign in",
    },
  },
  navigation: {
    overview: "Overview",
    settings: "Settings",
    emailImports: "Email Imports",
    notifications: "Notifications",
    signOut: "Sign Out",
    loggedOut: "Logged out successfully",
    toggleTheme: "Toggle theme",
    openNavigation: "Open navigation",
    closeNavigation: "Close navigation",
    closeOverlay: "Close navigation overlay",
  },
  dashboard: {
    overviewEyebrow: "Overview",
    title: "Subscription Overview",
    description:
      "Track active subscriptions, review spend totals, and manage imported or manual entries from one table.",
    stats: {
      totalSubscriptions: "Total Subscriptions",
      monthlyCost: "Monthly Cost",
      annualCost: "Annual Cost",
    },
    list: {
      title: "Your Subscriptions",
      description: "Manual and imported subscriptions appear in one list.",
      addButton: "Add Subscription",
      editTitle: "Edit Subscription",
      addTitle: "Add Subscription",
      searchPlaceholder: "Search by name or category",
      allFrequencies: "All Frequencies",
      noSubscriptions:
        "No subscriptions yet. Add one manually or connect Gmail or Outlook in Settings.",
      noMatches: "No subscriptions match the current filters.",
      table: {
        service: "Service",
        amount: "Amount",
        billing: "Billing",
        frequency: "Frequency",
        source: "Source",
        category: "Category",
        actions: "Actions",
      },
      billingDay: "Day {billingDate}",
      deleteConfirm: "Are you sure you want to delete this subscription?",
      addSuccess: "Subscription added successfully.",
      addFailed: "Failed to add subscription",
      updateSuccess: "Subscription updated successfully.",
      updateFailed: "Failed to update subscription",
      deleteSuccess: "Subscription deleted successfully.",
      deleteFailed: "Failed to delete subscription",
    },
    form: {
      serviceName: "Service Name",
      amount: "Amount",
      currency: "Currency",
      billingDate: "Billing Date",
      frequency: "Frequency",
      category: "Category",
      addSubmit: "Add Subscription",
      updateSubmit: "Update Subscription",
    },
    frequency: {
      monthly: "Monthly",
      annual: "Annual",
    },
    source: {
      manual: "Manual",
      googleImport: "Gmail import",
      microsoftImport: "Outlook import",
    },
    categories: {
      Entertainment: "Entertainment",
      Productivity: "Productivity",
      Utilities: "Utilities",
      Shopping: "Shopping",
      Health: "Health",
      Education: "Education",
      Finance: "Finance",
      Other: "Other",
    },
    emailImportCard: {
      title: "Email invoice import",
      description:
        "Connect Gmail or Outlook in Settings. Only invoices received after connection are imported.",
      cta: "Manage Email Imports",
    },
  },
  settings: {
    breadcrumb: {
      root: "Settings",
      imports: "Settings / Email imports",
      notifications: "Settings / Notifications",
      guide: "Settings / Guide",
    },
    callback: {
      connected: "{provider} connected",
      failed: "Email provider connection failed",
      connectionStatus: "Connection Status",
    },
    notifications: {
      title: "Reminder delivery",
      emailTitle: "Email Notifications",
      emailDescription: "Receive renewal reminders for tracked subscriptions.",
      timingTitle: "Reminder Timing",
      timingDescription:
        "Choose how many days before the billing date your reminder should arrive.",
      save: "Save Settings",
      saving: "Saving...",
      saveSuccess: "Notification settings saved successfully.",
      saveFailed: "Failed to save settings",
      options: {
        one: "1 day before",
        two: "2 days before",
        three: "3 days before",
        five: "5 days before",
        seven: "7 days before",
        fourteen: "14 days before",
      },
    },
    language: {
      title: "Language",
      description: "Choose the language used across the app and reminder emails.",
      saveFailed: "Failed to update language preference",
    },
    imports: {
      title: "Connected inboxes",
      description:
        "Connect Gmail to monitor new invoice emails. Imports only start after connection time and are grouped into recurring subscriptions automatically.",
      providers: {
        google: {
          description:
            "Watch new invoice and renewal emails arriving in Gmail after the connection is established.",
        },
        microsoft: {
          description: "Outlook import support is planned but not available yet.",
        },
      },
      testEmail: "Send Import Test Email",
      testingEmail: "Sending...",
      testEmailSuccess: "Subscription-style test email sent. Check your inbox.",
      testEmailFailed: "Failed to send test email",
      importPolicyEyebrow: "Import Policy",
      importPolicyTitle: "What happens after you connect",
      lastSyncUnavailable: "Not synced yet",
      connectionFields: {
        address: "Connected Address",
        lastSync: "Last Sync",
      },
      states: {
        comingSoon: "Coming soon",
        notConnected: "Not connected",
        needsAttention: "Needs attention",
        connected: "Connected",
      },
      actions: {
        connect: "Connect {provider}",
        reconnect: "Reconnect {provider}",
        redirecting: "Redirecting...",
        sync: "Sync Now",
        syncing: "Syncing...",
        disconnect: "Disconnect",
        disconnecting: "Disconnecting...",
      },
      syncComplete:
        "Sync complete. {imported} subscriptions imported or updated from {processed} new emails.",
      connectFailed: "Failed to connect {provider}",
      syncFailed: "Failed to sync {provider}",
      disconnectConfirm:
        "Disconnect {provider}? New invoice emails will stop importing.",
      disconnectSuccess: "{provider} disconnected.",
      disconnectFailed: "Failed to disconnect provider",
      policy: {
        startTitle: "Starts from connection time",
        startDescription:
          "Submind only watches emails that arrive after you connect the inbox. Existing mailbox history is left untouched.",
        signalsTitle: "Looks for billing signals",
        signalsDescription:
          "New invoice and renewal emails are scanned for recurring payment details before anything is added to your dashboard.",
        confidentTitle: "Imports only confident matches",
        confidentDescription:
          "High-confidence invoice matches create or update subscriptions automatically. Low-confidence emails are skipped.",
      },
    },
  },
  currency: {
    usd: {
      label: "US Dollar",
      description: "US Dollar",
    },
    eur: {
      label: "Euro",
      description: "Euro",
    },
    gbp: {
      label: "British Pound",
      description: "British Pound",
    },
  },
  emails: {
    reminder: {
      subject: "Reminder: {subscriptionName} renewal coming soon",
      brand: "Submind Reminder",
      title: "Upcoming Subscription Renewal",
      intro: "Your subscription is coming up for renewal soon.",
      service: "Service",
      amount: "Amount",
      billingDate: "Billing Date",
      billingDateValue: "Day {billingDate} of the month",
      daysUntil: "Days Until Renewal",
    },
    importTest: {
      subject:
        "Invoice from {serviceName} - {amount} monthly subscription",
      title: "Invoice from {serviceName}",
      subtitle: "Monthly subscription payment receipt",
      intro: "Your monthly subscription payment was successful.",
      plan: "Plan",
      amountCharged: "Amount charged",
      billingFrequency: "Billing frequency",
      monthly: "monthly",
      nextRenewal: "Next renewal date",
      footer:
        "This is a test invoice email from Submind so you can verify automatic subscription import.",
    },
  },
};

export default en;
